import { IRunPassage } from '@/models/Passage';
import {
  RunPassageColumn,
  RunPassageTimeZone,
  formatRunPassageTime,
  runPassageTimeLabel,
  sortRunPassages,
} from '@/utils/RunPassages';

/**
 * EN-1851: the tracksheet marker passages popover (hover on desktop, the
 * passages icon on touch). Its rows come from the run passages the section
 * below already loaded, so opening it makes no request. Pure helpers and a
 * small timer-driven hover state, so they can be unit tested.
 */

export const POPOVER_ROW_CAP = 5;
/** Hover this long on a marker cell before the popover opens. */
export const HOVER_OPEN_DELAY_MS = 400;
/** Grace period to move from the marker cell into the popover. */
export const HOVER_CLOSE_DELAY_MS = 250;

/** setPassageDialogSource values: which list the chart's next / previous walks. */
export const TRACKSHEET_SECTION_SOURCE = 'tracksheet_passages';
export const TRACKSHEET_POPOVER_SOURCE = 'tracksheet_marker_popover';

export interface MarkerPopoverRows {
  /** Newest first, at most the cap. */
  rows: IRunPassage[];
  /** Every passage at the marker. */
  total: number;
}

/** The popover's rows for one marker: its newest passages. */
export function markerPopoverRows(
  runPassages: readonly IRunPassage[],
  pipelineSectionMarkerId: number | null,
  cap: number = POPOVER_ROW_CAP
): MarkerPopoverRows {
  if (pipelineSectionMarkerId == null) return { rows: [], total: 0 };
  const atMarker = runPassages.filter(
    (r) => r.pipelineSectionMarkerId === pipelineSectionMarkerId
  );
  return {
    rows: sortRunPassages(atMarker, null, false).slice(0, Math.max(0, cap)),
    total: atMarker.length,
  };
}

/** 'Show all N below'. */
export function showAllLabel(total: number): string {
  return `Show all ${total} below`;
}

/** "Show all" always selects the marker (never toggles it off). */
export function selectionForShowAll(
  pipelineSectionMarkerId: number
): number | null {
  return pipelineSectionMarkerId;
}

export interface PopoverColumn {
  name: string;
  label: string;
  field: string;
  align: 'left' | 'center';
  format?: (value: string) => string;
  classes?: string;
  headerClasses?: string;
}

/** EN-1851: on a phone the popover's time wraps (date over time) ... */
export const POPOVER_TIME_CLASS = 'tracksheet-marker-passages-popover__time';
/** ... and its ML badges stack, so the ML column fits a 390 px screen. */
export const POPOVER_ML_CLASS = 'tracksheet-marker-passages-popover__ml';

/**
 * EN-1851: the popover's columns. Times as the tracksheet's Pass Time (UTC /
 * Local). On a narrow screen only Time, Verify and ML, so it fits a phone; a
 * row click there opens the waveform.
 */
export function markerPopoverColumns(
  zone: RunPassageTimeZone,
  narrow: boolean
): PopoverColumn[] {
  const columns: PopoverColumn[] = [
    {
      name: RunPassageColumn.time,
      label: runPassageTimeLabel(zone),
      field: 'passageTimeStamp',
      align: 'left',
      format: (value: string) => formatRunPassageTime(value, zone),
    },
    { name: RunPassageColumn.uid, label: 'UID', field: 'uid', align: 'left' },
    {
      name: RunPassageColumn.waveforms,
      label: 'Waveforms',
      field: 'graph-data',
      align: 'left',
    },
    {
      name: RunPassageColumn.verify,
      label: 'Verify',
      field: 'verify-data',
      align: 'center',
    },
    {
      name: RunPassageColumn.ml,
      label: 'ML',
      field: 'machine-learning-data',
      align: 'center',
    },
  ];
  if (!narrow) return columns;
  const phoneColumns: string[] = [
    RunPassageColumn.time,
    RunPassageColumn.verify,
    RunPassageColumn.ml,
  ];
  return columns
    .filter((column) => phoneColumns.includes(column.name))
    .map((column) => {
      const cls =
        column.name === RunPassageColumn.time
          ? POPOVER_TIME_CLASS
          : column.name === RunPassageColumn.ml
          ? POPOVER_ML_CLASS
          : undefined;
      return cls ? { ...column, classes: cls, headerClasses: cls } : column;
    });
}

export interface PopoverKeyEvent {
  key?: string;
  target?: { closest?: (selector: string) => unknown } | null;
}

/**
 * Esc closes the open popover (it has no focus of its own), unless a dialog
 * (the chart, a verify preview) has the key: Esc closes that first.
 */
export function isPopoverEscape(
  event: PopoverKeyEvent | null | undefined
): boolean {
  if (event?.key !== 'Escape') return false;
  return !event.target?.closest?.('.q-dialog');
}

export interface KeydownTarget {
  addEventListener: (type: 'keydown', listener: (event: any) => void) => void;
  removeEventListener: (
    type: 'keydown',
    listener: (event: any) => void
  ) => void;
}

/** A document keydown listener for Esc while the popover is open; returns its cleanup. */
export function listenForEscape(
  target: KeydownTarget,
  onEscape: () => void
): () => void {
  const listener = (event: PopoverKeyEvent) => {
    if (isPopoverEscape(event)) onEscape();
  };
  target.addEventListener('keydown', listener);
  return () => target.removeEventListener('keydown', listener);
}

/**
 * The open marker's row left the table (another page, the text filter): the
 * popover closes instead of coming back with the row.
 */
export function closesWhenRowLeaves(
  openMarkerId: number | null,
  leftMarkerId: number
): boolean {
  return openMarkerId === leftMarkerId;
}

export interface ResizeObserverLike {
  observe: (element: any) => void;
  disconnect: () => void;
}
export type ResizeObserverCtor = new (
  callback: () => void
) => ResizeObserverLike;

/**
 * Resizing the drawer moves the marker cell, and QMenu only follows window
 * resizes and scrolling: the open popover is placed again when the table
 * changes size. Returns the cleanup.
 */
export function followTableResize(
  table: unknown,
  onResize: () => void,
  Observer: ResizeObserverCtor | null | undefined = (globalThis as any)
    .ResizeObserver
): () => void {
  if (!table || !Observer) return () => undefined;
  const observer = new Observer(() => onResize());
  observer.observe(table);
  return () => observer.disconnect();
}

/** Only the list that opened the chart steps through next / previous. */
export function respondsToSwitchPassage(
  listSource: string,
  currentDialogSource: string | null | undefined
): boolean {
  return !!currentDialogSource && listSource === currentDialogSource;
}

/** Hover opens the popover for a mouse only; touch and pen use the icon. */
export function isHoverPointer(
  event: { pointerType?: string } | null
): boolean {
  return event?.pointerType === 'mouse';
}

export interface HoverTimers {
  set: (fn: () => void, ms: number) => any;
  clear: (handle: any) => void;
}

const DEFAULT_TIMERS: HoverTimers = {
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (handle) => clearTimeout(handle),
};

/**
 * Which marker's popover is open. Hover opens after a delay (moving across
 * rows does not flash popovers) and closes after a grace period unless the
 * pointer reaches the popover. The icon, or using the popover (chart, verify),
 * pins it: hover no longer closes or moves it, only the icon, close or
 * another icon does.
 */
export class MarkerHoverIntent {
  openMarkerId: number | null = null;
  pinned = false;

  constructor(
    private readonly onChange: (markerId: number | null) => void,
    private readonly timers: HoverTimers = DEFAULT_TIMERS,
    private readonly openDelayMs: number = HOVER_OPEN_DELAY_MS,
    private readonly closeDelayMs: number = HOVER_CLOSE_DELAY_MS
  ) {}

  private openTimer: any = null;
  private closeTimer: any = null;
  private pendingMarkerId: number | null = null;

  enterMarker(pipelineSectionMarkerId: number): void {
    if (this.pinned) return;
    this.cancelClose();
    if (this.openMarkerId === pipelineSectionMarkerId) {
      this.cancelOpen();
      return;
    }
    if (this.pendingMarkerId === pipelineSectionMarkerId) return;
    this.cancelOpen();
    this.pendingMarkerId = pipelineSectionMarkerId;
    this.openTimer = this.timers.set(() => {
      this.openTimer = null;
      this.pendingMarkerId = null;
      this.cancelClose();
      this.setOpen(pipelineSectionMarkerId);
    }, this.openDelayMs);
  }

  leaveMarker(): void {
    if (this.pinned) return;
    this.cancelOpen();
    this.scheduleClose();
  }

  enterPopover(): void {
    if (this.pinned) return;
    this.cancelClose();
  }

  leavePopover(): void {
    if (this.pinned) return;
    this.scheduleClose();
  }

  /** The passages icon: opens (pinned) or, on the pinned marker, closes. */
  toggleFromIcon(pipelineSectionMarkerId: number): void {
    this.cancelOpen();
    this.cancelClose();
    if (this.openMarkerId === pipelineSectionMarkerId && this.pinned) {
      this.close();
      return;
    }
    this.pinned = true;
    this.setOpen(pipelineSectionMarkerId);
  }

  /** The user is using the popover (chart, verify): keep it open. */
  pin(): void {
    if (this.openMarkerId === null) return;
    this.cancelOpen();
    this.cancelClose();
    this.pinned = true;
  }

  close(): void {
    this.cancelOpen();
    this.cancelClose();
    this.pinned = false;
    this.setOpen(null);
  }

  dispose(): void {
    this.cancelOpen();
    this.cancelClose();
  }

  private setOpen(markerId: number | null): void {
    if (this.openMarkerId === markerId) return;
    this.openMarkerId = markerId;
    this.onChange(markerId);
  }

  private scheduleClose(): void {
    if (this.openMarkerId === null || this.closeTimer !== null) return;
    this.closeTimer = this.timers.set(() => {
      this.closeTimer = null;
      this.pinned = false;
      this.setOpen(null);
    }, this.closeDelayMs);
  }

  private cancelOpen(): void {
    if (this.openTimer !== null) this.timers.clear(this.openTimer);
    this.openTimer = null;
    this.pendingMarkerId = null;
  }

  private cancelClose(): void {
    if (this.closeTimer !== null) this.timers.clear(this.closeTimer);
    this.closeTimer = null;
  }
}
